import { getDownloadURL, getStorage, ref, uploadBytes } from "firebase/storage";


export enum StorageType {
    BATCHES = "batches",
    PARTNERS = "partners",
    AWARENESS_CAMPAIGNS = "missions",
    AWARENESS_CAMPAIGNS_ADDRESSES = "mission-addresses",
};

export function getStorageURL(storage: StorageType) {
    return `gs://${process.env.REACT_APP_PROJECT_ID}-${storage}`;
}

export async function getPictureURL(imageURL: string, storageType: StorageType) {
    return await getFileURL(`${getStorageURL(storageType)}/${imageURL}`);
}

export async function getFileURL(filepath: string) {
    const storage = getStorage();
    const gsReference = ref(storage, filepath);

    return getDownloadURL(gsReference)
        .then((url) => {
            return url;
        })
        .catch((error) => {
            console.error(error);
            return null;
        });
}

export async function uploadFile(file: File, filepath: string) {
    const storage = getStorage();
    const storageRef = ref(storage, filepath);

    const snapshot = await uploadBytes(storageRef, file);
    const imageURL = await getDownloadURL(snapshot.ref);
    return imageURL;
}

/**
 * Uploads report images to the Google Cloud Storage bucket
 * @param missionID - The ID of the awareness campaign the address belongs to
 * @param addressID - The ID of the address the images belong to
 * @param images - An array of files representing the images to upload
 * @returns An array of uploaded image URLs
 */
export const uploadReportImages = async (missionID: string, addressID: string, images: File[]) => {
    const addressImagesUrl = getStorageURL(StorageType.AWARENESS_CAMPAIGNS_ADDRESSES);

    const uploadedImageURLs = await Promise.all(
        images.map(async (image) => {
            const filepath = `${missionID}/${addressID}/${Date.now()}_${image.name}`;
            const storagePath = `${addressImagesUrl}/${filepath}`;
            await uploadFile(image, storagePath);
            return filepath;
        })
    );

    return uploadedImageURLs;
}

/**
 * Processes image responses by uploading new files and combining with existing paths
 * @param missionID - The ID of the awareness campaign the address belongs to
 * @param addressID - The ID of the address the images belong to
 * @param imageResponses - Array of image response objects to process
 * @returns Array of all uploaded image paths
 */
export const processImageResponses = async (
    missionID: string, 
    addressID: string, 
    imageResponses: any[]
): Promise<string[]> => {
    let uploadedImagePaths: string[] = [];
    
    for (const imageResponse of imageResponses) {
        if (imageResponse.value && Array.isArray(imageResponse.value) && imageResponse.value.length > 0) {
            // Handle mixed array of Files and strings
            const existingPaths: string[] = [];
            const newFiles: File[] = [];
            
            for (const item of imageResponse.value) {
                if (item instanceof File) {
                    newFiles.push(item);
                } else if (typeof item === 'string') {
                    existingPaths.push(item);
                }
            }
            
            // Upload new files if any
            let newPaths: string[] = [];
            if (newFiles.length > 0) {
                newPaths = await uploadReportImages(missionID, addressID, newFiles);
            }
            
            // Combine existing and new paths
            const allPaths = [...existingPaths, ...newPaths];
            uploadedImagePaths = [...uploadedImagePaths, ...allPaths];
            
            // Update the response value with all paths
            imageResponse.value = allPaths as any;
        }
    }
    
    return uploadedImagePaths;
}
